/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, FormProvider, useForm } from "react-hook-form";
import styled from "styled-components";
import {
  Upload,
  Text,
  theme,
  Spacer,
  Heading,
  Box,
  Label,
} from "@nordcloud/gnui";
import { Provider } from "~/generated/graphql";
import { FormGroup, TransformableInput } from "~/components";
import { showError } from "~/services/toast";
import { isEmpty } from "~/tools";
import { StandardForm } from "~/views/accounts/components/StandardForm";
import { AccountContacts, AccountParams } from "~/views/accounts/types";
import { CopyBox } from "../components/CopyBox";
import { StepContainer } from "../components/StepContainer";
import {
  URL_GCP_CONSOLE,
  URL_GCP_STORAGE,
  URL_GCP_SERVICE_ACCOUNTS,
  URL_GCP_IAM,
  URL_GCP_IAM_CREDENTIALS,
} from "../constants";
import { trimTransform } from "../utils";
import { ERROR_READ_FILE, PLACEHOLDER_TEXT, GCP_ROLE } from "./constants";
import { readFile, prepareData, hasFiles } from "./helpers";
import { GoogleJsonData, GoogleUserInput } from "./types";
import { schemaCredsGcp } from "./validators";

const Error = styled(Text)`
  color: ${theme.color.text.error};
  padding: 0.5rem;
`;

const DottedList = styled.ul`
  & > li {
    list-style-type: disc;
    list-style-position: inside;
  }
`;

type Props = {
  onSubmit: (data: GoogleUserInput) => void;
  id: string;
  accountParams: AccountParams;
  additionalSettings: AccountContacts;
  setAdditionalSettings: (e: AccountContacts) => void;
};

type Data = {
  gcpFile: FileList;
  providerId: string;
  description?: string;
  name: string;
  displayName?: string;
  contactIds?: string;
  ownerId: string;
};

enum FormField {
  OWNER_ID = "ownerId",
  CONTACT_IDS = "contactIds",
  PROVIDER_ID = "providerId",
}

export function GcpAccountForm({
  onSubmit,
  accountParams,
  additionalSettings,
  setAdditionalSettings,
  ...props
}: Props) {
  const [filename, setFilename] = React.useState<string>("");

  const formMethods = useForm({
    resolver: yupResolver(schemaCredsGcp),
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    control,
  } = formMethods;

  React.useEffect(() => {
    register(FormField.OWNER_ID);
    register(FormField.CONTACT_IDS);
  }, [register]);

  const handleUpload = async ({
    gcpFile,
    providerId,
    description,
    displayName,
    contactIds,
    ownerId,
  }: Data) => {
    try {
      const json = await readFile<GoogleJsonData>(gcpFile[0]);
      const input: GoogleUserInput = {
        provider: Provider.Gcp,
        providerId,
        description,
        ownerId,
        contactIds,
        displayName,
        name: providerId,
        creds: prepareData(json),
      };
      onSubmit(input);
    } catch {
      showError(ERROR_READ_FILE);
      setFilename("");
      reset();
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    callback: (e: unknown) => void
  ) => {
    const fileList = e.target.files;
    if (fileList instanceof FileList && hasFiles(fileList)) {
      setFilename(fileList[0].name);
    }

    callback(fileList);
  };

  const placeholder = isEmpty(filename) ? PLACEHOLDER_TEXT : filename;

  return (
    <FormProvider {...formMethods}>
      <Spacer height="2rem" />
      <form onSubmit={handleSubmit(handleUpload)} {...props}>
        <StandardForm
          accountParams={accountParams}
          additionalSettings={additionalSettings}
          setAdditionalSettings={setAdditionalSettings}
        />

        <Spacer height="1rem" />
        <Heading level={3} marginBottom="spacing03">
          Account Credentials
        </Heading>
        <Text color={theme.color.text.text02}>
          To on-board GCP Organisation, please follow the next steps:
        </Text>

        <StepContainer counter={1}>
          <Text>
            Sign in to the{" "}
            <a href={URL_GCP_CONSOLE} target="_blank" rel="noreferrer">
              Google Cloud Platform
            </a>
          </Text>
          <FormGroup error={errors[FormField.PROVIDER_ID]}>
            <Label
              required
              htmlFor={FormField.PROVIDER_ID}
              name="Provide Organisation ID"
            />
            <TransformableInput
              name={FormField.PROVIDER_ID}
              transform={trimTransform}
              control={control}
              rules={{ required: true }}
              defaultValue={accountParams.providerId}
              placeholder="e.g. 123456789012"
              id={FormField.PROVIDER_ID}
            />
          </FormGroup>
        </StepContainer>

        <StepContainer counter={2}>
          <Text>Create a project for IBM Multicloud Accelerator</Text>
        </StepContainer>

        <StepContainer counter={3}>
          <Text>
            Go to the{" "}
            <a href={URL_GCP_STORAGE} target="_blank" rel="noreferrer">
              Cloud Storage
            </a>{" "}
            and Create Bucket with label:
          </Text>
          <Box boxStyle="grey" padding={theme.spacing.spacing02}>
            Key: nordcloud-purpose
            <br />
            Value: scanner
          </Box>
        </StepContainer>

        <StepContainer counter={4}>
          <Text>
            Go to the{" "}
            <a href={URL_GCP_SERVICE_ACCOUNTS} target="_blank" rel="noreferrer">
              Service Accounts
            </a>{" "}
            and create new Service Account
          </Text>
        </StepContainer>

        <StepContainer counter={5}>
          <Text>Go to the Service Account details and create new Key</Text>
          <Controller
            name="gcpFile"
            control={control}
            render={({ field: { onChange } }) => (
              <Upload
                id="gcpFile"
                placeholder={placeholder}
                onChange={(e) => handleInputChange(e, onChange)}
              />
            )}
          />
        </StepContainer>
        <StepContainer counter={6}>
          <Text>
            Go to the{" "}
            <a href={URL_GCP_IAM} target="_blank" rel="noreferrer">
              IAM
            </a>{" "}
            and on project level add following roles to the Service Account
          </Text>
          <CopyBox>{GCP_ROLE.tokenCreator}</CopyBox>
          <CopyBox>{GCP_ROLE.usageAdmin}</CopyBox>
          <CopyBox>{GCP_ROLE.objectCreator}</CopyBox>
        </StepContainer>
        <StepContainer counter={7}>
          <Text>
            Switch to the organisation level and add following roles to the
            Service Account
          </Text>
          <CopyBox>{GCP_ROLE.assetViewer}</CopyBox>
          <CopyBox>{GCP_ROLE.viewer}</CopyBox>
          <CopyBox>{GCP_ROLE.organizationViewer}</CopyBox>
        </StepContainer>
        <StepContainer counter={8}>
          <Text>
            Make sure that the following APIs are enabled on the project level:
          </Text>
          <DottedList>
            <li>
              <a
                href={URL_GCP_IAM_CREDENTIALS}
                target="_blank"
                rel="noreferrer"
              >
                IAM Service Account Credentials API
              </a>
            </li>
            <li>Service Usage API</li>
            <li>Storage API</li>
            <li>Cloud Asset API</li>
            <li>Cloud Resource Manager API</li>
            <li>Recommender API</li>
          </DottedList>
        </StepContainer>

        {errors.gcpFile && <Error>{errors.gcpFile.message}</Error>}
      </form>
    </FormProvider>
  );
}
